import { useEffect, useState, type FormEvent } from 'react';
import { Building2, Landmark, Pencil, Plus } from 'lucide-react';
import {
  createPaymentDestinationAccount,
  getPaymentDestinationAccounts,
  updatePaymentDestinationAccount,
  type PaymentDestinationAccount,
} from '@urbanik/api-client';
import { apiBase } from './api-base';
import './payment-destination-accounts.css';
const empty = {
  bankName: '',
  accountType: 'checking',
  accountNumber: '',
  holderName: '',
  holderTaxId: '',
  instructions: '',
  active: true,
  isDefault: false,
};
export function PaymentDestinationAccountsView() {
  const [items, setItems] = useState<PaymentDestinationAccount[]>([]),
    [editing, setEditing] = useState<PaymentDestinationAccount | null>(null),
    [form, setForm] = useState(empty),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState(''),
    [error, setError] = useState('');
  const load = async () => {
    try {
      setItems((await getPaymentDestinationAccounts(apiBase)).items);
      setError('');
    } catch (reason) {
      setError((reason as Error).message);
    }
  };
  useEffect(() => {
    void load();
  }, []);
  const begin = (item?: PaymentDestinationAccount) => {
    setEditing(item ?? null);
    setForm(
      item
        ? {
            bankName: item.bankName,
            accountType: item.accountType,
            accountNumber: item.accountNumber,
            holderName: item.holderName,
            holderTaxId: item.holderTaxId,
            instructions: item.instructions ?? '',
            active: item.active,
            isDefault: item.isDefault,
          }
        : empty,
    );
    setMessage('');
    setError('');
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      if (editing)
        await updatePaymentDestinationAccount(apiBase, editing.id, {
          ...form,
          version: editing.version,
        });
      else await createPaymentDestinationAccount(apiBase, form);
      setMessage(editing ? 'Cuenta actualizada.' : 'Cuenta receptora creada.');
      setEditing(null);
      setForm(empty);
      await load();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="payment-accounts">
      <header className="page-heading">
        <div>
          <p className="kicker">ADMINISTRACIÓN · PAGOS</p>
          <h1>Cuentas receptoras</h1>
          <p>Configura las cuentas que se muestran al registrar un comprobante.</p>
        </div>
        <button className="payment-accounts__primary" onClick={() => begin()}>
          <Plus />
          Nueva cuenta
        </button>
      </header>
      <section className="payment-accounts__list" aria-label="Cuentas receptoras">
        {items.length ? (
          items.map((item) => (
            <article key={item.id} className={!item.active ? 'is-inactive' : ''}>
              <Landmark />
              <div>
                <div className="payment-accounts__badges">
                  {item.isDefault ? <span>Predeterminada</span> : null}
                  <span>{item.active ? 'Activa' : 'Inactiva'}</span>
                </div>
                <h2>{item.bankName}</h2>
                <p>
                  {item.accountType === 'checking'
                    ? 'Corriente'
                    : item.accountType === 'savings'
                      ? 'Ahorros'
                      : 'Pasarela'}{' '}
                  · {item.accountNumber}
                </p>
                <small>
                  {item.holderName} · {item.holderTaxId} · {item.currency}
                </small>
              </div>
              <button onClick={() => begin(item)} aria-label={`Editar ${item.bankName}`}>
                <Pencil />
                Editar
              </button>
            </article>
          ))
        ) : (
          <div className="payment-accounts__empty">
            <Building2 />
            <h2>No hay cuentas receptoras</h2>
            <p>Crea la primera cuenta para habilitar el registro de comprobantes.</p>
          </div>
        )}
      </section>
      <section className="payment-accounts__form">
        <header>
          <div>
            <span>{editing ? 'EDICIÓN' : 'NUEVA CUENTA'}</span>
            <h2>{editing ? 'Editar cuenta receptora' : 'Registrar cuenta receptora'}</h2>
          </div>
        </header>
        <form onSubmit={submit}>
          <label>
            <span>Banco</span>
            <input
              required
              minLength={2}
              value={form.bankName}
              onChange={(e) => setForm({ ...form, bankName: e.target.value })}
            />
          </label>
          <label>
            <span>Tipo de cuenta</span>
            <select
              value={form.accountType}
              onChange={(e) => setForm({ ...form, accountType: e.target.value })}
            >
              <option value="checking">Corriente</option>
              <option value="savings">Ahorros</option>
              <option value="card_gateway">Pasarela de tarjeta</option>
            </select>
          </label>
          <label>
            <span>Número o identificador</span>
            <input
              required
              minLength={3}
              value={form.accountNumber}
              onChange={(e) => setForm({ ...form, accountNumber: e.target.value })}
            />
          </label>
          <label>
            <span>Titular</span>
            <input
              required
              minLength={2}
              value={form.holderName}
              onChange={(e) => setForm({ ...form, holderName: e.target.value })}
            />
          </label>
          <label>
            <span>RUC o identificación</span>
            <input
              required
              minLength={5}
              value={form.holderTaxId}
              onChange={(e) => setForm({ ...form, holderTaxId: e.target.value })}
            />
          </label>
          <label className="wide">
            <span>Instrucciones para el socio</span>
            <textarea
              value={form.instructions}
              onChange={(e) => setForm({ ...form, instructions: e.target.value })}
            />
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            <span>Cuenta activa</span>
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={form.isDefault}
              onChange={(e) => setForm({ ...form, isDefault: e.target.checked })}
            />
            <span>Usar como predeterminada</span>
          </label>
          <div className="wide payment-accounts__actions">
            <button type="button" onClick={() => begin()}>
              Limpiar
            </button>
            <button className="payment-accounts__primary" disabled={busy}>
              {busy ? 'Guardando…' : editing ? 'Guardar cambios' : 'Crear cuenta'}
            </button>
          </div>
        </form>
        {error ? <p className="payment-accounts__error">{error}</p> : null}
        {message ? <p className="payment-accounts__success">{message}</p> : null}
      </section>
    </div>
  );
}
